import { useEffect, useMemo, useState } from 'react'
import {
  addSpaceBetweenCamelCase,
  AgxBodyText,
  AgxRow,
  NswContractStage,
  QldContractStage,
} from '@urbanx/agx-ui-components'
import useSelectedContractStage from 'hooks/useSelectedContractStage'
import placeHolderImage from 'assets/images/placeholder-property.png'
import { ContractActionRequestContractAction } from '@urbanx/agx.campaigns.client'
import { AgxTypography } from '@urbanx/agx.ui-components2'

interface ContractImageProps {
  imageUrl?: string
}

const actionItems = {
  [ContractActionRequestContractAction.MarkAsAccepted]: {
    title: 'Accepted',
    action: ContractActionRequestContractAction.MarkAsAccepted,
  },
  [ContractActionRequestContractAction.Cancelled]: {
    title: 'Cancelled',
    action: ContractActionRequestContractAction.Cancelled,
  },
  [ContractActionRequestContractAction.MarkAsUnconditional]: {
    title: 'Unconditional',
    action: ContractActionRequestContractAction.MarkAsUnconditional,
  },
  [ContractActionRequestContractAction.Terminated]: {
    title: 'Terminated',
    action: ContractActionRequestContractAction.Terminated,
  },
  [ContractActionRequestContractAction.MarkSettled]: {
    title: 'Settled',
    action: ContractActionRequestContractAction.MarkSettled,
  },
  [ContractActionRequestContractAction.RevertToAccepted]: {
    title: 'Revert to Accepted',
    action: ContractActionRequestContractAction.RevertToAccepted,
  },
  [ContractActionRequestContractAction.RevertToUnconditional]: {
    title: 'Revert to Unconditional',
    action: ContractActionRequestContractAction.RevertToUnconditional,
  },
}

const ContractImage = ({ imageUrl }: ContractImageProps) => {
  const { contractStage, performContractAction } = useSelectedContractStage()
  const [updatingContractStage, setUpdatingContractStage] = useState(false)

  useEffect(() => {
    setUpdatingContractStage(false)
  }, [contractStage])

  const availableActions = useMemo(() => {
    switch (contractStage) {
      case NswContractStage.ContractRequested:
      case NswContractStage.AwaitingExchange:
        return [
          actionItems[ContractActionRequestContractAction.MarkAsAccepted],
          actionItems[ContractActionRequestContractAction.Cancelled],
        ]
      case NswContractStage.Accepted:
      case QldContractStage.Accepted:
        return [
          actionItems[ContractActionRequestContractAction.MarkAsUnconditional],
          actionItems[ContractActionRequestContractAction.Terminated],
        ]
      case NswContractStage.Unconditional:
      case QldContractStage.Unconditional:
        return [
          actionItems[ContractActionRequestContractAction.MarkSettled],
          actionItems[ContractActionRequestContractAction.RevertToAccepted],
          actionItems[ContractActionRequestContractAction.Terminated],
        ]
      case NswContractStage.Settled:
      case QldContractStage.Settled:
        return [
          actionItems[
            ContractActionRequestContractAction.RevertToUnconditional
          ],
        ]
      default:
        return []
    }
  }, [contractStage])

  return (
    <AgxRow extraClasses="propertyImageContainer">
      {contractStage && (
        <div className="propertyImageStatusContainer fill">
          <div className="campaignImage-imageBadgeContainer">
            <AgxTypography
              id={'imgBadgeTxt'}
              type={'tag'}
              size={'medium'}
              color="var(--text-brand-main)"
              flexShrink={0}
            >
              {addSpaceBetweenCamelCase(contractStage)}
            </AgxTypography>
          </div>
          {!updatingContractStage &&
            availableActions.map(({ title, action }, ind) => (
              <div
                key={`action-${ind}`}
                className="propertyImageStatus"
                onClick={() => {
                  performContractAction(action)
                  setUpdatingContractStage(true)
                }}
              >
                <AgxBodyText>{title}</AgxBodyText>
              </div>
            ))}
        </div>
      )}
      <img alt="" className="propertyImage" src={imageUrl ?? placeHolderImage} />
    </AgxRow>
  )
}

export default ContractImage
